import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  FormControlLabel,
  IconButton,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import {
  ChevronLeft,
  ChevronRight,
  AutoAwesome as AiIcon,
  Add as AddIcon,
  Close as ClearIcon,
  Email as EmailIcon,
} from "@mui/icons-material";
import AddressbookPanelShell from "../addressbook/panels/AddressbookPanelShell";
import ContactCard from "../../components/component-contact/ContactCard";
import { useContactCategories } from "../../components/component-contact/useContactCategories";
import { apiGetContactsCollection, apiSendEmailToCategory, apiUpdateContact } from "../../graphQL/addressbook/collections/contacts";
import { toContactCard } from "../addressbook/presenters";

const PAGE_SIZE = 50;

const sx = {
  empty: { py: 4, display: "flex", justifyContent: "center" },
  cardList: { display: "flex", flexDirection: "column", gap: 1 },
  pagerLabel: { color: "text.secondary", fontSize: 11, minWidth: 72, textAlign: "center" },
};

const EmptyState = ({ message }) => (
  <Box sx={sx.empty}>
    <Typography sx={{ color: "text.secondary", fontSize: 13 }}>{message}</Typography>
  </Box>
);

const createBulkDryRunResult = (recipients) => ({
  totalMatched: recipients.length,
  totalAttempted: recipients.length,
  totalSent: 0,
  totalFailed: 0,
  totalSkipped: 0,
  dryRun: true,
  results: recipients.map((recipient) => ({
    contactId: recipient.id,
    email: recipient.email,
    status: "dry-run",
    error: "",
    messageId: "",
  })),
});

const truthyFlag = (value) => value === true || String(value || "").trim().toLowerCase() === "true";

const isDoNotContact = (contact) => {
  const data = contact?.data && typeof contact.data === "object" ? contact.data : {};
  const emailPrefs = data?.email && typeof data.email === "object" ? data.email : {};
  const emails = Array.isArray(contact?.reach?.emails) ? contact.reach.emails : [];
  const state = String(contact?.state || contact?.status || "").trim().toLowerCase();
  return Boolean(
    truthyFlag(contact?.doNotContact)
    || truthyFlag(contact?.do_not_contact)
    || truthyFlag(data?.doNotContact)
    || truthyFlag(data?.do_not_contact)
    || truthyFlag(data?.emailOptOut)
    || truthyFlag(data?.unsubscribed)
    || truthyFlag(emailPrefs?.doNotContact)
    || truthyFlag(emailPrefs?.optOut)
    || truthyFlag(emailPrefs?.unsubscribed)
    || emails.some((e) => truthyFlag(e?.doNotContact) || truthyFlag(e?.optOut) || truthyFlag(e?.unsubscribed))
    || ["do-not-contact", "do_not_contact", "unsubscribed", "opted-out", "opted_out"].includes(state)
  );
};

const DEFAULT_EMAIL_WORKFLOW_OPTIONS = {
  compliance: {
    skipDoNotContact: true,
    unsubscribeStatus: "not-configured",
  },
  retryPolicy: {
    maxRetries: 3,
    retryDelayMinutes: 15,
  },
};

const toTelHref = (phone) => {
  const raw = String(phone || "").trim();
  if (!raw) return "";
  const cleaned = raw.replace(/[^+\d]/g, "");
  return cleaned ? `tel:${cleaned}` : "";
};

const isMobileDevice = () => {
  if (typeof window === "undefined") return false;
  const ua = navigator.userAgent || navigator.vendor || "";
  return /Android|iPhone|iPad|iPod|Mobile|IEMobile|Opera Mini/i.test(ua);
};

const LeadContactListPanel = ({
  authorId,
  onClose,
  onSelect,
  onOpenDetails,
  selectedId,
  onCompose,
  onComposeText,
  onAdd,
  onMagic,
  onDelete,
  searchFilter = "",
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [contacts, setContacts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  // Full contact docs matching the current criteria — fetched lazily, only
  // when the bulk-email dialog opens (do-not-contact checks read data.*).
  const [bulkPool, setBulkPool] = useState(null);
  const [bulkPoolLoading, setBulkPoolLoading] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [qrPhone, setQrPhone] = useState("");
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [bulkSending, setBulkSending] = useState(false);
  const [bulkFrom, setBulkFrom] = useState("");
  const [bulkReplyTo, setBulkReplyTo] = useState("");
  const [bulkSubject, setBulkSubject] = useState("");
  const [bulkBody, setBulkBody] = useState("");
  const [bulkActuallySendEmails, setBulkActuallySendEmails] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkError, setBulkError] = useState("");

  const authUser = useSelector((s) => s.auth.user);
  const userDomain = authUser?.domain || "";

  const contactCategories = useContactCategories(authorId);

  useEffect(() => {
    setPage(1);
  }, []);
  useEffect(() => {
    setPage(1);
  }, [searchFilter]);

  const serverFilters = useMemo(() => {
    const f = {};
    if (userDomain) f.domain = userDomain;
    if (searchFilter.trim()) f.text = searchFilter.trim();
    return f;
  }, [userDomain, searchFilter]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const { items, total: serverTotal } = await apiGetContactsCollection({
          authorId: null,
          pagination: { page, limit: PAGE_SIZE },
          ordering: { field: "updatedAt", order: "Desc" },
          filters: serverFilters,
        });
        if (cancelled) return;
        const rows = (items || []).map((c) => ({
          contact: c,
          role: c?.identity?.person?.role || c?.role || c?.category || "",
        }));
        setContacts(rows);
        setTotal(Number(serverTotal) || 0);
      } catch (err) {
        if (!cancelled) {
          console.error("Failed to load contacts:", err);
          setError("Could not load contacts.");
          setContacts([]);
          setTotal(0);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [authorId, serverFilters, page]);

  const cards = useMemo(
    () => contacts.map(({ contact, role }) => ({ ...toContactCard(contact), role, _raw: contact })),
    [contacts]
  );

  // Bulk email targets EVERYTHING matching the criteria (not just the visible
  // page), and do-not-contact checks read the data.* bag, so the pool is a
  // separate full-document fetch made only when the dialog opens.
  useEffect(() => {
    if (!bulkOpen) return undefined;
    let cancelled = false;
    (async () => {
      setBulkPoolLoading(true);
      try {
        const { items } = await apiGetContactsCollection({
          full: true,
          authorId: null,
          pagination: { page: 1, limit: 1000 },
          ordering: { field: "updatedAt", order: "Desc" },
          filters: serverFilters,
        });
        if (!cancelled) setBulkPool(items || []);
      } catch (err) {
        if (!cancelled) {
          console.error("Failed to load bulk-email pool:", err);
          setBulkPool([]);
        }
      } finally {
        if (!cancelled) setBulkPoolLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [bulkOpen, serverFilters]);

  const filteredRecipients = useMemo(() => {
    return (bulkPool || [])
      .map((raw) => {
        const emails = Array.isArray(raw?.reach?.emails) ? raw.reach.emails : [];
        const firstEmail = emails.find((e) => e?.value && String(e.value).trim())?.value || "";
        return {
          id: raw?.id,
          category: raw?.category || "",
          email: String(firstEmail || "").trim(),
          raw,
        };
      })
      .filter((r) => r.id && r.email);
  }, [bulkPool]);
  const blockedRecipients = useMemo(
    () => filteredRecipients.filter((recipient) => isDoNotContact(recipient.raw)),
    [filteredRecipients]
  );
  const eligibleRecipients = useMemo(
    () => filteredRecipients.filter((recipient) => !isDoNotContact(recipient.raw)),
    [filteredRecipients]
  );
  const criteriaSummary = useMemo(() => {
    const parts = [];
    if (userDomain && userDomain !== "All") parts.push(`domain=${userDomain}`);
    if (searchFilter?.trim()) parts.push(`search="${searchFilter.trim()}"`);
    if (!parts.length) return "all contacts in current scope";
    return parts.join(" · ");
  }, [userDomain, searchFilter]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const pageItems = cards;

  useEffect(() => { if (page > totalPages) setPage(totalPages); }, [page, totalPages]);

  const handlePhoneClick = (phone) => {
    const telHref = toTelHref(phone);
    if (!telHref) return;
    if (isMobileDevice()) {
      window.location.href = telHref;
      return;
    }
    setQrPhone(String(phone || ""));
    setQrOpen(true);
  };

  const qrValue = toTelHref(qrPhone);
  const qrImageUrl = qrValue
    ? `https://quickchart.io/qr?size=220&text=${encodeURIComponent(qrValue)}`
    : "";

  const handleContactCategoryChange = async (contact, category) => {
    if (!contact?.id || !category) return;
    const value = String(category.id || category.value || category.name || "");
    try {
      await apiUpdateContact({ id: contact.id, category: value });
      setContacts((prev) =>
        prev.map((row) =>
          row?.contact?.id === contact.id
            ? { ...row, contact: { ...row.contact, category: value } }
            : row
        )
      );
    } catch (err) {
      console.error("Failed to update contact category:", err);
    }
  };

  const handleOpenBulk = () => {
    setBulkFrom(String(authUser?.email || ""));
    setBulkReplyTo("");
    setBulkSubject(`Update for ${criteriaSummary}`);
    setBulkBody("");
    setBulkActuallySendEmails(false);
    setBulkResult(null);
    setBulkError("");
    setBulkOpen(true);
  };

  const canSendBulk = !!authorId && !!bulkFrom.trim() && !!bulkSubject.trim() && !!bulkBody.trim() && eligibleRecipients.length > 0 && !bulkSending && !bulkPoolLoading;

  const handleConfirmBulkSend = async () => {
    if (!canSendBulk) return;
    setBulkSending(true);
    setBulkError("");
    setBulkResult(null);
    const domainForSend = userDomain && userDomain !== "All" ? userDomain : undefined;
    try {
      if (!bulkActuallySendEmails) {
        const dryRunResult = await apiSendEmailToCategory({
          authorId,
          domain: domainForSend,
          recipientIds: eligibleRecipients.map((recipient) => recipient.id),
          subject: bulkSubject.trim(),
          body: bulkBody,
          from: bulkFrom.trim(),
          replyTo: bulkReplyTo.trim() || undefined,
          dryRun: true,
          campaignId: `match:${domainForSend || "all"}`,
          criteria: criteriaSummary,
          ...DEFAULT_EMAIL_WORKFLOW_OPTIONS,
        });
        setBulkResult(dryRunResult || {
          ...createBulkDryRunResult(eligibleRecipients),
          totalMatched: filteredRecipients.length,
          totalSkipped: filteredRecipients.length - eligibleRecipients.length,
          results: [
            ...createBulkDryRunResult(eligibleRecipients).results,
            ...blockedRecipients.map((recipient) => ({
              contactId: recipient.id,
              email: recipient.email,
              status: "do-not-contact",
              error: "Skipped because contact is marked do-not-contact or unsubscribed.",
              messageId: "",
            })),
          ],
        });
        setBulkConfirmOpen(false);
        return;
      }

      const sendResult = await apiSendEmailToCategory({
        authorId,
        domain: domainForSend,
        recipientIds: eligibleRecipients.map((recipient) => recipient.id),
        subject: bulkSubject.trim(),
        body: bulkBody,
        from: bulkFrom.trim(),
        replyTo: bulkReplyTo.trim() || undefined,
        dryRun: false,
        campaignId: `match:${domainForSend || "all"}`,
        criteria: criteriaSummary,
        ...DEFAULT_EMAIL_WORKFLOW_OPTIONS,
      });
      setBulkResult(sendResult || null);
      setBulkConfirmOpen(false);
    } catch (err) {
      console.error("Bulk send from match failed:", err);
      setBulkError(err?.message || "Bulk send failed.");
    } finally {
      setBulkSending(false);
    }
  };

  return (
    <AddressbookPanelShell
      onClose={onClose}
      titleContent={
        <Box sx={{ display: "flex", alignItems: "center", flex: 1 }} />
      }
      headerActions={
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <IconButton
            size="small"
            onClick={() => onAdd?.()}
            title="Add contact"
            sx={{
              width: 24, height: 24,
              backgroundColor: "#16a34a", color: "#FFFFFF",
              "&:hover": { backgroundColor: "#15803d" },
            }}
          >
            <AddIcon sx={{ fontSize: 15 }} />
          </IconButton>
          <IconButton
            size="small"
            onClick={handleOpenBulk}
            title="Bulk email filtered contacts"
            sx={{
              width: 24, height: 24,
              backgroundColor: "#2563EB", color: "#FFFFFF",
              "&:hover": { backgroundColor: "#1D4ED8" },
            }}
          >
            <EmailIcon sx={{ fontSize: 14 }} />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => onMagic?.()}
            title="Magic"
            sx={{
              width: 24, height: 24,
              backgroundColor: "#7c3aed", color: "#FFFFFF",
              "&:hover": { backgroundColor: "#6d28d9" },
            }}
          >
            <AiIcon sx={{ fontSize: 15 }} />
          </IconButton>
        </Box>
      }
      footerLeft={
        <Typography sx={{ color: "text.secondary", fontSize: 11 }}>
          {pageItems.length} / {total} contact{total !== 1 ? "s" : ""}
        </Typography>
      }
      footerRight={
        <>
          <IconButton size="small" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} aria-label="Previous page">
            <ChevronLeft fontSize="small" />
          </IconButton>
          <Typography sx={sx.pagerLabel}>{page} / {totalPages}</Typography>
          <IconButton size="small" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages} aria-label="Next page">
            <ChevronRight fontSize="small" />
          </IconButton>
        </>
      }
    >
      {loading && (
        <Box sx={{ py: 4, display: "flex", justifyContent: "center", alignItems: "center", gap: 1.25 }}>
          <CircularProgress size={20} />
          <Typography sx={{ color: "text.secondary", fontSize: 13 }}>Loading contacts...</Typography>
        </Box>
      )}

      {!loading && error && <EmptyState message={error} />}

      {!loading && !error && total === 0 && (
        <EmptyState
          message={searchFilter.trim()
            ? "No contacts match the current search."
            : "No contacts found for this domain."}
        />
      )}

      {!loading && !error && pageItems.length > 0 && (
        <Box sx={sx.cardList}>
          {pageItems.map((card) => (
            <ContactCard
              key={card.id}
              contact={card}
              role={card.role}
              selected={selectedId === card.id}
              categories={contactCategories}
              onClick={() => onSelect?.(card._raw || card)}
              onDoubleClick={() => onOpenDetails?.(card._raw || card)}
              onPhoneClick={handlePhoneClick}
              onCompose={onCompose ? () => onCompose(card._raw || card) : undefined}
              onComposeText={onComposeText ? () => onComposeText(card._raw || card) : undefined}
              onCategoryChange={handleContactCategoryChange}
              onDelete={onDelete ? () => onDelete(card._raw || card) : undefined}
            />
          ))}
        </Box>
      )}

      <Dialog open={qrOpen} onClose={() => setQrOpen(false)} maxWidth="xs" fullWidth>
        <Box sx={{ p: 2, textAlign: "center", position: "relative" }}>
          <IconButton
            size="small"
            onClick={() => setQrOpen(false)}
            sx={{ position: "absolute", top: 8, right: 8, color: "#64748B" }}
            title="Close"
          >
            <ClearIcon sx={{ fontSize: 16 }} />
          </IconButton>
          <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 1 }}>Call From Your Phone</Typography>
          {qrImageUrl && (
            <Box
              component="img"
              src={qrImageUrl}
              alt="Phone call QR code"
              sx={{ width: 220, height: 220, mx: "auto", display: "block", borderRadius: 1, border: "1px solid #E2E8F0" }}
            />
          )}
          <Typography sx={{ mt: 1, fontSize: 12, color: "text.secondary" }}>{qrPhone}</Typography>
        </Box>
      </Dialog>

      <Dialog open={bulkOpen} onClose={() => setBulkOpen(false)} maxWidth="sm" fullWidth>
        <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>
            Bulk Email (Filtered Match Contacts)
          </Typography>
          <Typography sx={{ fontSize: 12, color: "#64748B" }}>
            {bulkPoolLoading
              ? "Loading recipients..."
              : `${eligibleRecipients.length} eligible contact${eligibleRecipients.length === 1 ? "" : "s"} · ${blockedRecipients.length} do-not-contact · ${filteredRecipients.length} total with email.`}
          </Typography>
          <Typography sx={{ fontSize: 11, color: "#475569" }}>
            Criteria: {criteriaSummary}
          </Typography>
          <TextField size="small" label="From" value={bulkFrom} onChange={(e) => setBulkFrom(e.target.value)} />
          <TextField size="small" label="Reply-To (optional)" value={bulkReplyTo} onChange={(e) => setBulkReplyTo(e.target.value)} />
          <TextField size="small" label="Subject" value={bulkSubject} onChange={(e) => setBulkSubject(e.target.value)} />
          <TextField
            multiline
            minRows={6}
            maxRows={14}
            label="Body"
            value={bulkBody}
            onChange={(e) => setBulkBody(e.target.value)}
            placeholder="Use {{firstName}}, {{lastName}}, {{fullName}}, {{company}}, {{email}}"
          />
          <Box sx={{ p: 1.25, border: "1px solid #E2E8F0", borderRadius: 1, backgroundColor: bulkActuallySendEmails ? "#FFF7ED" : "#F8FAFC" }}>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={bulkActuallySendEmails}
                  onChange={(e) => setBulkActuallySendEmails(e.target.checked)}
                />
              }
              label={<Typography sx={{ fontSize: 12, fontWeight: 700 }}>Actually send emails</Typography>}
              sx={{ m: 0 }}
            />
            <Typography sx={{ mt: 0.5, fontSize: 11, color: "#64748B" }}>
              {bulkActuallySendEmails
                ? "Live mode: contacts will be staged, emailed, and restored after dispatch."
                : "Dry run: validates this filtered workflow without sending emails or changing contact states."}
            </Typography>
          </Box>
          {bulkError ? (
            <Typography sx={{ fontSize: 12, color: "#B91C1C" }}>{bulkError}</Typography>
          ) : null}
          {bulkResult ? (
            <Box sx={{ p: 1, border: "1px solid #BBF7D0", borderRadius: 1, backgroundColor: "#F0FDF4" }}>
              <Typography sx={{ fontSize: 12, color: "#166534", fontWeight: 700 }}>
                {bulkResult?.dryRun ? "Dry run complete" : "Send complete"}
              </Typography>
              <Typography sx={{ fontSize: 12, color: "#166534" }}>
                Attempted: {bulkResult?.totalAttempted || 0} · Sent: {bulkResult?.totalSent || 0} · Failed: {bulkResult?.totalFailed || 0} · Skipped: {bulkResult?.totalSkipped || 0}
              </Typography>
            </Box>
          ) : null}
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button size="small" onClick={() => setBulkOpen(false)} sx={{ textTransform: "none" }}>Close</Button>
            <Button
              size="small"
              variant="contained"
              onClick={() => setBulkConfirmOpen(true)}
              disabled={!canSendBulk}
              sx={{ textTransform: "none", backgroundColor: "#2563EB", "&:hover": { backgroundColor: "#1D4ED8" } }}
            >
              {bulkSending ? (bulkActuallySendEmails ? "Sending..." : "Running...") : (bulkActuallySendEmails ? "Send bulk email" : "Run dry run")}
            </Button>
          </Box>
        </Box>
      </Dialog>

      <Dialog open={bulkConfirmOpen} onClose={() => setBulkConfirmOpen(false)} maxWidth="xs" fullWidth>
        <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>
            Confirm Bulk Send
          </Typography>
          <Typography sx={{ fontSize: 12, color: "#475569" }}>
            {bulkActuallySendEmails ? "Send" : "Dry run"} this email workflow for {eligibleRecipients.length} eligible contact{eligibleRecipients.length === 1 ? "" : "s"}?
          </Typography>
          <Typography sx={{ fontSize: 11, color: "#64748B" }}>
            Criteria: {criteriaSummary}
          </Typography>
          <Typography sx={{ fontSize: 11, color: "#64748B" }}>
            {bulkActuallySendEmails
              ? "We will target only this filtered set, then restore contact types automatically."
              : "No emails will be sent; lifecycle progress will be recorded as a dry run."}
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 0.5 }}>
            <Button size="small" onClick={() => setBulkConfirmOpen(false)} sx={{ textTransform: "none" }}>
              Cancel
            </Button>
            <Button size="small" variant="contained" onClick={handleConfirmBulkSend} disabled={bulkSending} sx={{ textTransform: "none" }}>
              {bulkSending ? (bulkActuallySendEmails ? "Sending..." : "Running...") : "Confirm"}
            </Button>
          </Box>
        </Box>
      </Dialog>
    </AddressbookPanelShell>
  );
};

export default LeadContactListPanel;
